import type { Dispatch, SetStateAction, RefObject } from 'react'
import { Box, HStack, Text, Icon, IconButton, Tooltip, useColorMode } from '@chakra-ui/react'
import { ChevronRight, ChevronDown, X } from 'lucide-react'
import { countFolderShares, isEmptyPreservedFolder, type FolderNode } from '@/lib/utils/folder-tree'
import { type ShareSummary } from '@/lib/api/keys'
import { ShareItem } from './ShareItem'

interface FolderRowProps {
  node: FolderNode<ShareSummary>
  depth: number
  // Folder tree state
  expandedFolders: Set<string>
  toggleFolder: (path: string) => void
  preservedFolders: Set<string>
  deletePreservedFolder: (path: string) => void
  // Drag-and-drop
  dragOverFolderPath: string | null
  setDragOverFolderPath: Dispatch<SetStateAction<string | null>>
  onFolderDrop: (targetPath: string, e: React.DragEvent) => void
  // Share item props (passed through)
  selectedFileId?: string
  onSelectFile: (shareId: string) => void
  onCloneShare?: (shareId: string) => void
  getDisplayName: (share: ShareSummary) => string
  isEncrypted: (share: ShareSummary) => boolean
  getShareUrl: (share: ShareSummary, mode?: 'edit') => string
  handleCopyLink: (share: ShareSummary) => void
  handleRevoke: (shareId: string) => void
  handleViewDetails: (share: ShareSummary) => void
  isCopied: (shareId: string) => boolean
  draggingShareId: string | null
  setDraggingShareId: Dispatch<SetStateAction<string | null>>
  dragImageRef: RefObject<HTMLDivElement | null>
  handleDragEnd: () => void
}

export function FolderRow({
  node,
  depth,
  expandedFolders,
  toggleFolder,
  preservedFolders,
  deletePreservedFolder,
  dragOverFolderPath,
  setDragOverFolderPath,
  onFolderDrop,
  selectedFileId,
  onSelectFile,
  onCloneShare,
  getDisplayName,
  isEncrypted,
  getShareUrl,
  handleCopyLink,
  handleRevoke,
  handleViewDetails,
  isCopied,
  draggingShareId,
  setDraggingShareId,
  dragImageRef,
  handleDragEnd,
}: FolderRowProps) {
  const { colorMode } = useColorMode()

  const isExpanded = expandedFolders.has(node.path)
  const totalShares = countFolderShares(node)
  const isEmptyPreserved = isEmptyPreservedFolder(
    node,
    totalShares,
    preservedFolders,
  )

  const shareItemProps = {
    selectedFileId,
    onSelectFile,
    onCloneShare,
    getDisplayName,
    isEncrypted,
    getShareUrl,
    handleCopyLink,
    handleRevoke,
    handleViewDetails,
    isCopied,
    draggingShareId,
    setDraggingShareId,
    dragImageRef,
    handleDragEnd,
  }

  const folderProps = {
    expandedFolders,
    toggleFolder,
    preservedFolders,
    deletePreservedFolder,
    dragOverFolderPath,
    setDragOverFolderPath,
    onFolderDrop,
    ...shareItemProps,
  }

  return (
    <Box>
      <HStack
        pr={2}
        py={1}
        pl={`${(2 + depth * 4) * 4}px`}
        borderRadius="md"
        cursor="pointer"
        onClick={() => toggleFolder(node.path)}
        _hover={{
          bg: colorMode === 'dark' ? 'stone.700' : 'stone.200',
        }}
        transition="all 0.15s ease"
        role="group"
        onDragOver={(e) => {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'move'
          setDragOverFolderPath(node.path)
        }}
        onDragLeave={() => {
          setDragOverFolderPath((prev) =>
            prev === node.path ? null : prev,
          )
        }}
        onDrop={(e) => onFolderDrop(node.path, e)}
        bg={
          dragOverFolderPath === node.path
            ? colorMode === 'dark'
              ? 'whiteAlpha.100'
              : 'blackAlpha.50'
            : undefined
        }
        outline={dragOverFolderPath === node.path ? '2px solid' : 'none'}
        outlineColor={
          dragOverFolderPath === node.path
            ? colorMode === 'dark'
              ? 'brand.400'
              : 'brand.600'
            : 'transparent'
        }
        outlineOffset="-2px"
      >
        <Icon
          as={isExpanded ? ChevronDown : ChevronRight}
          boxSize={4}
          color={colorMode === 'dark' ? 'stone.400' : 'stone.500'}
          flexShrink={0}
        />
        <Text
          flex={1}
          fontSize="sm"
          fontWeight="medium"
          noOfLines={1}
          color={colorMode === 'dark' ? 'stone.300' : 'stone.600'}
        >
          {node.name}
        </Text>
        {totalShares > 0 && (
          <Text fontSize="2xs" color="stone.500">
            {totalShares}
          </Text>
        )}
        {isEmptyPreserved && (
          <Tooltip label="Delete empty folder">
            <IconButton
              aria-label="Delete folder"
              icon={<Icon as={X} boxSize={3} />}
              variant="ghost"
              size="xs"
              opacity={0}
              _groupHover={{ opacity: 0.7 }}
              _hover={{
                opacity: 1,
                color: 'red.500',
              }}
              onClick={(e) => {
                e.stopPropagation()
                deletePreservedFolder(node.path)
              }}
            />
          </Tooltip>
        )}
      </HStack>
      {isExpanded && (
        <>
          {Array.from(node.children.values())
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((child) => (
              <FolderRow
                key={child.path}
                node={child}
                depth={depth + 1}
                {...folderProps}
              />
            ))}
          {node.shares.map((share) => (
            <ShareItem
              key={share.id}
              share={share}
              depth={depth + 1}
              {...shareItemProps}
            />
          ))}
        </>
      )}
    </Box>
  )
}
